import React from "react";
import { motion } from "framer-motion";

const STEPS = [
  {
    n: "01",
    title: "Farm",
    desc: "High-altitude estates in Idukki & Wayanad. Shade-grown under native rainforest canopy at 800–1200 m.",
  },
  {
    n: "02",
    title: "Sorting",
    desc: "Hand-sorted within 48 hours of harvest. Damaged, splits and immature pods removed manually.",
  },
  {
    n: "03",
    title: "Grading",
    desc: "Calibrated by sieve size — 6mm, 7mm, 8mm, Jumbo. Colour, density and aroma cross-checked.",
  },
  {
    n: "04",
    title: "Packaging",
    desc: "Vacuum-sealed food-grade bags. 1 kg, 5 kg, 25 kg and bulk sacks. Batch & lot traceability.",
  },
  {
    n: "05",
    title: "Delivery",
    desc: "Same-week dispatch across Bangalore. Pan-Karnataka logistics with cold-chain on request.",
  },
];

export const FarmToBusiness = () => {
  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="section-pad bg-[#FAF8F2] relative"
    >
      <div className="container-luxury">
        <div className="max-w-2xl mx-auto text-center mb-16 md:mb-24">
          <div className="heading-eyebrow justify-center">
            <span className="line" />
            <span className="text">From Estate To Enterprise</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-[#0E4D3D] leading-tight">
            The Farm-to-Business Journey
          </h2>
          <p className="mt-6 text-[#4A4A4A] text-base md:text-lg font-light leading-relaxed">
            Five precise stages between the picker's basket and your warehouse —
            each step audited for quality, aroma and consistency.
          </p>
        </div>

        {/* Desktop horizontal */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Gold connecting line */}
            <div className="absolute top-12 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#C9A227] to-transparent" />
            <div className="grid grid-cols-5 gap-6">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.15, duration: 0.7 }}
                  className="flex flex-col items-center text-center group"
                  data-testid={`journey-step-${i}`}
                >
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-[#FAF8F2] border border-[#C9A227]/60 flex items-center justify-center group-hover:bg-[#0E4D3D] group-hover:border-[#0E4D3D] transition-all duration-500">
                      <span className="font-serif text-2xl text-[#0E4D3D] group-hover:text-[#C9A227] transition-colors">
                        {s.n}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="absolute top-12 -right-6 w-4 h-4 rotate-45 border-t border-r border-[#C9A227]/50 hidden xl:block" />
                    )}
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-[#0E4D3D]">
                    {s.title}
                  </h3>
                  <div className="mt-2 h-px w-8 bg-[#C9A227]" />
                  <p className="mt-4 text-[#4A4A4A] text-sm leading-relaxed font-light max-w-[200px]">
                    {s.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="lg:hidden relative pl-8">
          <div className="absolute left-3 top-2 bottom-2 w-px bg-gradient-to-b from-[#C9A227] via-[#C9A227]/40 to-transparent" />
          <div className="space-y-10">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="relative"
              >
                <div className="absolute -left-[1.65rem] top-1.5 w-3 h-3 rounded-full bg-[#C9A227] ring-4 ring-[#FAF8F2]" />
                <div className="label-overline text-[#C9A227]">{s.n}</div>
                <h3 className="font-serif text-xl text-[#0E4D3D] mt-1">
                  {s.title}
                </h3>
                <p className="mt-2 text-[#4A4A4A] text-sm leading-relaxed font-light">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
